import {
  AlertTriangle,
  ArrowRight,
  Brain,
  Building2,
  Calendar,
  CheckCircle,
  ChevronDown,
  Clock,
  Database,
  DollarSign,
  FileText,
  Globe,
  Link2,
  ListFilter,
  MapPin,
  Search,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CharCounter,
  EmptyState,
  Input,
  PageLayout,
  ResponsiveGrid,
  Select,
  Skeleton,
  Textarea,
} from "../components";
import { ScheduleSuggestionsSkeleton } from "../components/skeletons";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { useContentLibrary } from "../hooks/useContentLibrary";
import {
  showError,
  showProgress,
  showSuccess,
  showToastWarning,
} from "../shared/alertService";
import apiService, { API_ENDPOINTS } from "../shared/apiService";
import { PLATFORMS_LIST, getPlatformName } from "../utils/platformIcons";

// Limites de caracteres pros campos de texto livre que viram prompt da IA.
// "recommended" é só aviso (âmbar); "hard" bloqueia a geração (vermelho).
const PERSONA_LIMIT = { recommended: 150, hard: 300 };
const OBJECTIVES_LIMIT = { recommended: 150, hard: 300 };
const PRODUCT_LIMIT = { recommended: 100, hard: 200 };

interface ContentItem {
  id: number;
  name: string;
  type: string;
  themes: string[];
  platforms: string[];
  content_preview: string;
  business_type: string;
  usage_stats: {
    posts_generated: number;
    last_used: string;
  };
}

interface ScheduleSuggestion {
  date: string;
  time: string;
  platform: string;
  content_type: string;
  theme: string;
  suggested_content: string;
  hashtags: string[];
  call_to_action: string;
  best_practice: string;
  engagement_prediction: string;
  week_number: number;
  day_of_week: string;
  optimal_reasons: string[];
}

interface ScheduleResponse {
  schedule_suggestions: ScheduleSuggestion[];
  posts_created?: {
    total_created: number;
    posts?: Array<{
      platform: string;
      content_preview: string;
      scheduled_date: string;
      scheduled_time: string;
      theme: string;
      week_number: number;
      post_id?: number;
      content_id?: number | null;
      status?: string;
      review_flags?: string[];
    }>;
    auto_created: boolean;
  };
  summary: {
    total_posts: number;
    platforms: string[];
    start_date: string;
    weeks: number;
    business_type: string;
    posts_per_week: number;
    posts_created?: number;
    distribution_strategy?: string;
  };
  best_practices_applied: string[];
}

const ScheduleSuggestions: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeContext } = useActiveContext();
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<ScheduleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Estados para seletor de conteúdos
  const {
    contentLibrary,
    loadContentLibrary,
    loading: loadingContents,
  } = useContentLibrary({ loadAll: true });
  const [selectedContentIds, setSelectedContentIds] = useState<number[]>([]);
  const [contentFilter, setContentFilter] = useState({
    content_type: "",
    search: "",
  });

  // Mapear contentLibrary para o formato esperado
  const availableContents: ContentItem[] = contentLibrary.map(
    (content: any) => ({
      id: content.id,
      name: content.name,
      type: content.type,
      themes: [],
      platforms: content.platforms || [],
      content_preview: content.original_data || "",
      business_type: content.business_type || "b2b",
      usage_stats: {
        posts_generated: 0,
        last_used: content.created_at || "",
      },
    }),
  );

  const [sourceMode, setSourceMode] = useState<"creative" | "library">(
    "library",
  );
  const [useContentLibraryEnabled, setUseContentLibraryEnabled] =
    useState(true);
  const [showAdvanced, setShowAdvanced] = useState(true);

  // Sync sourceMode with useContentLibraryEnabled
  useEffect(() => {
    setUseContentLibraryEnabled(sourceMode === "library");
  }, [sourceMode]);

  // Parâmetros do formulário
  const [platforms, setPlatforms] = useState<string[]>(["linkedin"]);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [weeks, setWeeks] = useState(1);
  const [businessType, setBusinessType] = useState<"b2b" | "b2c" | "local">(
    "b2b",
  );
  const [postsPerWeek, setPostsPerWeek] = useState(7);
  const [distributionStrategy, setDistributionStrategy] = useState<
    "balanced" | "sequential" | "thematic"
  >("balanced");
  const [campaignType, setCampaignType] = useState<"organic" | "paid">(
    "organic",
  );
  const [dailyBudget, setDailyBudget] = useState<number>(20);
  const [generateCreativeAi, setGenerateCreativeAi] = useState<boolean>(false);
  const CREATIVE_AI_MAX_POSTS = 20;
  const [objectives, setObjectives] = useState<string>(
    t("Engajamento e geração de leads"),
  );
  const [persona, setPersona] = useState<string>(
    t("Decisores B2B em tecnologia"),
  );
  const [product, setProduct] = useState<string>("");
  const [useRegisteredIcp, setUseRegisteredIcp] = useState<boolean>(false);
  const [sources, setSources] = useState<string>("");
  const [includeUTM, setIncludeUTM] = useState<boolean>(true);
  const [timeSlots, setTimeSlots] = useState<string[]>([
    "09:00",
    "13:00",
    "17:00",
  ]);
  const [timeSlotsRaw, setTimeSlotsRaw] = useState<string>(
    timeSlots.join("\n"),
  );
  const [timeSlotsWarning, setTimeSlotsWarning] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<Record<string, string>>(
    {},
  );

  // Modelo de IA (LLM) para o cronograma
  // Padrão: OpenAI gpt-5.4-nano
  const [aiModel, setAiModel] = useState<string>("gpt-5.4-nano");
  const [creatorBenchmarks, setCreatorBenchmarks] = useState<any[]>([]);
  const [creatorBenchmarksError, setCreatorBenchmarksError] = useState(false);
  const [selectedCreatorBenchmark, setSelectedCreatorBenchmark] =
    useState<string>("");

  const fetchCreatorBenchmarks = React.useCallback(async () => {
    setCreatorBenchmarksError(false);
    try {
      const response = await apiService.get("/creator-benchmarks");
      if (response && response.success) {
        setCreatorBenchmarks(response.benchmarks || []);
      } else {
        setCreatorBenchmarksError(true);
      }
    } catch (err) {
      console.warn("Erro ao buscar benchmarks de criadores:", err);
      setCreatorBenchmarksError(true);
    }
  }, []);

  useEffect(() => {
    fetchCreatorBenchmarks();
  }, [fetchCreatorBenchmarks]);

  // Custo estimado (créditos) — busca o custo base de cada tipo de uso uma
  // única vez; a multiplicação por modelo de IA real acontece no backend
  // (baseada no modelo padrão da organização, não no seletor deste formulário),
  // então isso é uma estimativa, não o valor exato que será cobrado.
  const [contentAiCost, setContentAiCost] = useState<number | null>(null);
  const [imageGenCost, setImageGenCost] = useState<number | null>(null);

  useEffect(() => {
    const fetchCosts = async () => {
      try {
        const [contentRes, imageRes] = await Promise.all([
          apiService.post<any>("/api/credits/preview-cost", {
            usage_type: "content_ai",
          }),
          apiService.post<any>("/api/credits/preview-cost", {
            usage_type: "image_generation",
          }),
        ]);
        if (contentRes?.success) setContentAiCost(contentRes.total_cost);
        if (imageRes?.success) setImageGenCost(imageRes.total_cost);
      } catch (err) {
        console.warn(
          "Não foi possível carregar custo estimado de créditos:",
          err,
        );
      }
    };
    fetchCosts();
  }, []);

  const distributionOptions = [
    {
      id: "balanced",
      name: t("Balanceada"),
      description: t("Distribui os conteúdos de forma equilibrada"),
    },
    {
      id: "sequential",
      name: t("Sequencial"),
      description: t("Usa os conteúdos em ordem sequencial"),
    },
    {
      id: "thematic",
      name: t("Temática"),
      description: t("Agrupa por temas similares"),
    },
  ];

  // Carregar conteúdos quando a biblioteca for ativada
  useEffect(() => {
    if (useContentLibraryEnabled) {
      loadContentLibrary();
    }
  }, [useContentLibraryEnabled, loadContentLibrary]);

  useEffect(() => {
    // Simular carregamento inicial
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Resetar estado quando o contexto ativo mudar
  useEffect(() => {
    setSuggestions(null);
    setSelectedContentIds([]);
    setError(null);
    setSuccess(null);
  }, [activeContext]);

  const handleContentSelection = (contentId: number) => {
    setSelectedContentIds((prev) =>
      prev.includes(contentId)
        ? prev.filter((id) => id !== contentId)
        : [...prev, contentId],
    );
  };

  const pollScheduleTask = (
    taskId: string,
    progressModal: any,
    onSuccess: (result: any) => void,
  ) => {
    let progress = 5;
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiService.get(
          `/content/generate-schedule/status/${taskId}`,
        );

        if (response.status === "completed") {
          clearInterval(pollInterval);

          const result = response.result;

          // A tarefa em si "completou" (não lançou exceção), mas a validação
          // interna (ex.: limite de posts com Creative AI, crédito insuficiente)
          // pode ter recusado a geração sem criar nada — trata como erro.
          if (result?.success === false) {
            progressModal.close();
            setLoading(false);
            const errMsg =
              result.error || t("Não foi possível gerar o cronograma.");
            showError(errMsg, t("Falha na Geração"));
            setError(errMsg);
            return;
          }

          progressModal.update(100, t("Concluído!"));
          onSuccess(result);

          progressModal.close();
          setLoading(false);

          const postsCreated = result.posts_created?.total_created || 0;
          const creditsConsumed = result.credits_used || 0;
          const postsLine =
            postsCreated > 0
              ? t("\n✅ {count} posts criados como 'Pendentes'.", {
                  count: postsCreated,
                })
              : "";

          // Cronograma "Paga": cada post gerado já vira uma campanha-RASCUNHO
          // local (sem chamar Meta/LinkedIn, sem custo) pronta pra revisão em
          // /ads/new — não lançamos nada automaticamente.
          const campaignsCreated = result.campaigns_created?.total_created || 0;
          const createdCampaigns: Array<{ campaign_id?: number }> =
            result.campaigns_created?.campaigns || [];
          const campaignsLine =
            campaignsCreated > 0
              ? t(
                  "\n💰 {count} campanhas criadas como rascunho — revise e lance em Anúncios.",
                  { count: campaignsCreated },
                )
              : "";

          const creativeAiLine = generateCreativeAi
            ? t(
                "\n🎨 As imagens estão sendo geradas em segundo plano — acompanhe em Posts.",
              )
            : "";

          // O revisor automático (Creative AI) pode gerar avisos por post (texto
          // longo demais pra plataforma, termo de risco, contraste ruim etc.) —
          // sem isso, esses avisos ficavam só no banco, sem ninguém ver.
          const postsWithWarnings = (result.posts_created?.posts || []).filter(
            (p: { review_flags?: string[] }) =>
              p.review_flags && p.review_flags.length > 0,
          );
          const reviewFlagsLine =
            postsWithWarnings.length > 0
              ? t(
                  "\n⚠️ {count} post(s) com avisos de revisão — confira antes de aprovar.",
                  { count: postsWithWarnings.length },
                )
              : "";

          const msg = t(
            "🎉 Cronograma gerado com sucesso! {count} posts sugeridos.{postsLine}{campaignsLine}{creativeAiLine}{reviewFlagsLine}\n\n⚡ **Créditos Consumidos:** {credits} créditos de IA.",
            {
              count: result.schedule_suggestions?.length || 0,
              postsLine,
              campaignsLine,
              creativeAiLine,
              reviewFlagsLine,
              credits: creditsConsumed,
            },
          );

          if (postsWithWarnings.length > 0) {
            const sampleFlags = Array.from(
              new Set(
                postsWithWarnings.flatMap(
                  (p: { review_flags?: string[] }) => p.review_flags || [],
                ),
              ),
            ).slice(0, 3);
            showToastWarning(
              t("{count} post(s) gerados com avisos do revisor:\n• {flags}", {
                count: postsWithWarnings.length,
                flags: sampleFlags.join("\n• "),
              }),
            );
          }

          showSuccess(msg, t("Geração Concluída"), {
            timer: 8000,
            timerProgressBar: true,
          }).then(() => {
            if (campaignsCreated === 1 && createdCampaigns[0]?.campaign_id) {
              // Só 1 campanha: abre direto no wizard pra revisão.
              navigate(`/ads/new?draftId=${createdCampaigns[0].campaign_id}`);
            } else if (campaignsCreated > 1) {
              // Várias: melhor ver a lista e abrir "Editar" em cada uma.
              navigate("/ads");
            } else {
              navigate("/posts");
            }
          });
        } else if (
          response.status === "error" ||
          response.status === "failed"
        ) {
          clearInterval(pollInterval);
          progressModal.close();
          const errorMsg =
            response.error ||
            t("Falha no processador de tarefas em segundo plano.");
          showError(errorMsg, t("Erro de Geração"));
          setError(errorMsg);
          setLoading(false);
        } else {
          // Ainda executando ou pendente
          if (progress < 95) {
            progress += progress < 50 ? 5 : 2;
          }
          let msg = t("Configurando parâmetros...");
          if (progress > 20) msg = t("IA analisando seu nicho...");
          if (progress > 55) msg = t("Gerando sugestões criativas...");
          if (progress > 80) msg = t("Quase pronto! Organizando calendário...");
          progressModal.update(progress, msg);
        }
      } catch (err: any) {
        console.warn("Erro ao buscar status do cronograma:", err);
      }
    }, 2500);
  };

  const generateContentBasedSchedule = async () => {
    if (selectedContentIds.length === 0) {
      setError(t("Selecione pelo menos um conteúdo"));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    const progressModal = showProgress(
      t("Gerando Estratégia"),
      t("Analisando conteúdos selecionados..."),
    );

    try {
      // Obter token de autenticação
      const token =
        localStorage.getItem("access_token") || localStorage.getItem("token");

      if (!token) {
        setError(
          t("Token de autenticação não encontrado. Faça login novamente."),
        );
        return;
      }

      const response = await apiService.post(
        API_ENDPOINTS.CONTENT_SELECTOR.GENERATE_SCHEDULE_FROM_SELECTION,
        {
          selected_content_ids: selectedContentIds,
          platforms: platforms,
          start_date: startDate,
          weeks,
          posts_per_week: postsPerWeek,
          business_type: businessType,
          distribution_strategy: distributionStrategy,
          frequency: "daily",
          time_slots: timeSlots,
          campaign_type: campaignType,
          daily_budget: campaignType === "paid" ? dailyBudget : undefined,
          generate_creative_ai: generateCreativeAi,
          objectives,
          persona,
          product: product.trim() || undefined,
          use_registered_icp: useRegisteredIcp,
          sources: sources
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean),
          include_utm: includeUTM,
          auto_create_posts: true,
          ai_model: aiModel,
          creator_benchmark_name: selectedCreatorBenchmark,
        },
        { retry: false },
      );

      if (response && response.task_id) {
        pollScheduleTask(response.task_id, progressModal, (result: any) => {
          const scheduleResponse: ScheduleResponse = {
            schedule_suggestions: result.schedule_suggestions || [],
            posts_created: result.posts_created,
            summary: result.summary || {
              total_posts: result.schedule_suggestions?.length || 0,
              platforms: Array.from(
                new Set(
                  (result.schedule_suggestions || []).map(
                    (s: any) => s.platform,
                  ),
                ),
              ),
              start_date: startDate,
              weeks,
              business_type: businessType,
              posts_per_week: postsPerWeek,
            },
            best_practices_applied:
              result.ai_optimization_applied?.best_practices_applied ||
              result.optimization_applied?.best_practices_applied ||
              [],
          };
          setSuggestions(scheduleResponse);
        });
      } else {
        throw new Error(
          response?.error ||
            t("Não foi possível enfileirar a tarefa de geração."),
        );
      }
    } catch (error) {
      progressModal.close();
      const errorMsg =
        error instanceof Error
          ? error.message
          : t("Erro desconhecido ao gerar com biblioteca");
      showError(errorMsg, t("Falha na Geração"));
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handlePlatformToggle = (platformId: string) => {
    setPlatforms((prev) => {
      // Se estiver desmarcando a única plataforma selecionada, manter pelo menos uma
      if (prev.includes(platformId) && prev.length === 1) {
        return prev; // Não permite desmarcar se é a única
      }

      return prev.includes(platformId)
        ? prev.filter((p) => p !== platformId)
        : [...prev, platformId];
    });
  };

  const generateSuggestions = async () => {
    if (platforms.length === 0) {
      setError(t("Selecione pelo menos uma plataforma"));
      return;
    }

    // Validar data de início (ignorando fuso/horário, comparar só a data)
    try {
      const [year, month, day] = startDate.split("-").map(Number);
      const startDateObj = new Date(year, (month || 1) - 1, day || 1);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDateObj.getTime() < today.getTime()) {
        setError(t("A data de início não pode ser no passado"));
        return;
      }
    } catch {
      // Se der erro ao interpretar a data, deixa o backend tratar
    }

    // Validar semanas e posts por semana
    if (weeks < 1 || weeks > 52) {
      setError(t("O período deve ser entre 1 e 52 semanas"));
      return;
    }

    if (postsPerWeek < 1 || postsPerWeek > 20) {
      setError(t("Posts por semana deve ser entre 1 e 20"));
      return;
    }

    if (generateCreativeAi) {
      const estimatedTotalPosts = calculateTotalPosts();
      if (estimatedTotalPosts > CREATIVE_AI_MAX_POSTS) {
        setError(
          t(
            "Com o Creative AI ligado, o limite é {max} posts por geração (esse cronograma geraria {count}). Reduza o período/posts por semana ou desligue o Creative AI.",
            { max: CREATIVE_AI_MAX_POSTS, count: estimatedTotalPosts },
          ),
        );
        return;
      }
    }

    // Se estiver usando biblioteca de conteúdos, usar a função de conteúdos selecionados
    if (useContentLibraryEnabled && selectedContentIds.length > 0) {
      await generateContentBasedSchedule();
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);

    const progressModal = showProgress(
      t("Criando Cronograma"),
      t("Iniciando motores de inteligência..."),
    );

    try {
      // Obter token de autenticação
      const token =
        localStorage.getItem("access_token") || localStorage.getItem("token");

      if (!token) {
        setError(
          t("Token de autenticação não encontrado. Faça login novamente."),
        );
        return;
      }

      const response = await apiService.post(
        API_ENDPOINTS.CONTENT_SELECTOR.GENERATE_SCHEDULE_FROM_SELECTION,
        {
          selected_content_ids: [], // Empty para cronograma tradicional
          platforms,
          start_date: startDate,
          weeks,
          business_type: businessType,
          posts_per_week: postsPerWeek,
          distribution_strategy: distributionStrategy,
          frequency: "daily",
          time_slots: timeSlots,
          campaign_type: campaignType,
          daily_budget: campaignType === "paid" ? dailyBudget : undefined,
          generate_creative_ai: generateCreativeAi,
          objectives,
          persona,
          product: product.trim() || undefined,
          use_registered_icp: useRegisteredIcp,
          sources: sources
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean),
          include_utm: includeUTM,
          auto_create_posts: true, // Importante: ativar criação automática
          ai_model: aiModel,
          creator_benchmark_name: selectedCreatorBenchmark,
        },
        { retry: false },
      );

      if (response && response.task_id) {
        pollScheduleTask(response.task_id, progressModal, (result: any) => {
          const scheduleResponse: ScheduleResponse = {
            schedule_suggestions: result.schedule_suggestions || [],
            posts_created: result.posts_created,
            summary: result.summary || {
              total_posts: result.schedule_suggestions?.length || 0,
              platforms: platforms,
              start_date: startDate,
              weeks: weeks,
              business_type: businessType,
              posts_per_week: postsPerWeek,
            },
            best_practices_applied: result.best_practices_applied || [],
          };
          setSuggestions(scheduleResponse);
        });
      } else {
        throw new Error(
          response?.error ||
            t("Não foi possível enfileirar a tarefa de geração."),
        );
      }
    } catch (error) {
      progressModal.close();
      const errorMsg =
        error instanceof Error
          ? error.message
          : t("Erro desconhecido ao gerar sugestões");
      showError(errorMsg, t("Erro de Geração"));
      setError(errorMsg);
      setLoading(false);
    }
  };

  const getPlatformColor = (platform: string) => {
    const colors = {
      linkedin: "bg-blue-500",
      facebook: "bg-blue-600",
      instagram: "bg-pink-500",
      google: "bg-green-500",
      tiktok: "bg-gray-800",
    };
    return colors[platform as keyof typeof colors] || "bg-gray-500";
  };

  const getEngagementColor = (prediction: string) => {
    const colors = {
      Alto: "bg-green-100 text-green-800",
      Médio: "bg-yellow-100 text-yellow-800",
      Moderado: "bg-orange-100 text-orange-800",
    };
    return (
      colors[prediction as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  // Função para formatar data ISO para brasileiro
  const formatDateToBrazilian = (isoDate: string) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Função para converter data brasileira para ISO
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  // Calcular total de posts que serão gerados — o backend cria 1 post por
  // plataforma em cada slot (weeks * posts_per_week * plataformas), não só
  // weeks * posts_per_week (isso subestimava o total quando havia mais de
  // uma rede selecionada).
  const calculateTotalPosts = () => {
    return weeks * postsPerWeek * Math.max(1, platforms.length);
  };

  // Estimativa de custo em créditos: com Creative AI ligado, cada post cobra
  // content_ai + image_generation individualmente; sem isso, o backend cobra
  // um valor fixo (1x content_ai) pro lote inteiro, não por post.
  const calculateEstimatedCreditCost = (): number | null => {
    if (contentAiCost === null) return null;
    if (generateCreativeAi) {
      if (imageGenCost === null) return null;
      return (contentAiCost + imageGenCost) * calculateTotalPosts();
    }
    return contentAiCost;
  };

  // Função para calcular data final
  const calculateEndDate = () => {
    const startDt = new Date(startDate);
    const endDt = new Date(
      startDt.getTime() + (weeks * 7 - 1) * 24 * 60 * 60 * 1000,
    );
    return endDt;
  };

  // Ordem de exibição dos toggles de rede social (grid de 3 colunas):
  // linha 1: LinkedIn, Facebook, Facebook Stories
  // linha 2: YouTube, Instagram, Instagram Stories
  // linha 3: Google My Business, TikTok
  const platformDisplayOrder = [
    "linkedin",
    "facebook",
    "facebook_stories",
    "youtube",
    "instagram",
    "instagram_stories",
    "google_my_business",
    "tiktok",
  ];
  const orderedPlatformsList = [...PLATFORMS_LIST].sort(
    (a: any, b: any) =>
      platformDisplayOrder.indexOf(a.id) - platformDisplayOrder.indexOf(b.id),
  );

  return (
    <PageLayout
      title={t("Cronograma de Postagens com IA")}
      subtitle={t(
        "Cronogramas otimizados por IA: melhores horários e temas para suas redes sociais.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2 py-4"
      loading={loading}
      skeleton={<ScheduleSuggestionsSkeleton />}
    >
      <div className="w-full">
        {/* SEÇÃO 1 e 2 lado a lado: mesma altura, sem precisar de scroll pra ver as 2 */}
        <div className="-mt-2 grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2 items-stretch">
          <Card
            variant="default"
            className="h-full p-5 sm:p-6 rounded-2xl border-purple-200 shadow-xs bg-white"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-linear-to-br from-purple-500 to-indigo-600 text-white shadow-xs shrink-0">
                <Calendar className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                  {t("Base do Cronograma")}
                </h2>
                <p className="text-sm text-gray-500 leading-none mt-1">
                  {t("Defina o alcance da sua estratégia")}
                </p>
              </div>
            </div>

            {/* Seleção de Plataformas */}
            <div className="mb-5">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2.5">
                {t("Redes Sociais Ativas")}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {orderedPlatformsList.map((platform: any) => {
                  const IconComponent = platform.icon;
                  const isActive = platforms.includes(platform.id);
                  return (
                    <button
                      key={platform.id}
                      onClick={() => handlePlatformToggle(platform.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all duration-200 ${
                        isActive
                          ? "border-purple-600 bg-purple-50/50"
                          : "border-gray-100 bg-gray-50/50 hover:border-purple-200 hover:bg-white"
                      }`}
                    >
                      <div
                        className={`p-1 rounded-lg shrink-0 transition-colors ${isActive ? "bg-purple-600 text-white" : "bg-white text-gray-400"}`}
                      >
                        <IconComponent className="h-3.5 w-3.5" />
                      </div>
                      <span
                        className={`text-xs font-semibold whitespace-nowrap ${isActive ? "text-purple-700" : "text-gray-500"}`}
                      >
                        {t(getPlatformName(platform.id))}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t("Início do Ciclo")}
                type="date"
                value={startDate}
                onChange={handleDateChange}
                inputSize="sm"
                hint={t("A partir de quando o calendário começa.")}
              />
              <Select
                label={t("Nicho de Atuação")}
                value={businessType}
                onChange={(value) => setBusinessType(value as any)}
                options={[
                  {
                    value: "b2b",
                    label: t("B2B - Business to Business"),
                    icon: <Building2 />,
                  },
                  {
                    value: "b2c",
                    label: t("B2C - Consumidor Final"),
                    icon: <Users />,
                  },
                  {
                    value: "local",
                    label: t("Negócio Local / Regional"),
                    icon: <MapPin />,
                  },
                ]}
                size="sm"
                hint={t("Ajusta os gatilhos usados pela IA ao seu setor.")}
              />
              <Select
                label={t("Duração Total")}
                value={weeks.toString()}
                onChange={(value) => setWeeks(Number(value))}
                options={[
                  { value: "1", label: t("1 Semana (Sprint)") },
                  { value: "2", label: t("2 Semanas (Planejamento)") },
                  { value: "4", label: t("4 Semanas (Ciclo Mensal)") },
                  { value: "8", label: t("8 Semanas (Estratégico)") },
                ]}
                size="sm"
                hint={t("O horizonte temporal do planejamento.")}
              />
              <Select
                label={t("Frequência de Postagem")}
                value={postsPerWeek.toString()}
                onChange={(value) => setPostsPerWeek(Number(value))}
                options={[
                  { value: "1", label: t("1 Post por Semana") },
                  { value: "3", label: t("3 Posts por Semana") },
                  { value: "5", label: t("5 Posts por Semana") },
                  { value: "7", label: t("Diário (7 Posts/Sem)") },
                  { value: "14", label: t("Intenso (2/dia)") },
                ]}
                size="sm"
                hint={t("Volume de conteúdo por rede social.")}
              />
            </div>

            {/* Resumo compacto - substitui o antigo painel "Raio-X Estratégico" */}
            <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-xs text-gray-500">{t("Postagens")}</span>
                <span className="text-sm font-bold text-gray-900">
                  {calculateTotalPosts()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-xs text-gray-500">{t("Canais")}</span>
                <span className="text-sm font-bold text-gray-900">
                  {platforms.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-xs text-gray-500">{t("Janela")}</span>
                <span className="text-sm font-bold text-gray-900">
                  {weeks}w
                </span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-gray-400">
                  {formatDateToBrazilian(startDate)}
                </span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="text-xs font-semibold text-indigo-600">
                  {formatDateToBrazilian(calculateEndDate().toISOString())}
                </span>
              </div>
            </div>
          </Card>

          {/* SEÇÃO 2: FONTE DE CONHECIMENTO (Antigo Passo 3) */}
          <Card
            variant="default"
            className="h-full flex flex-col p-5 sm:p-6 rounded-2xl border-purple-200 shadow-xl shadow-gray-200/5 bg-white"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-linear-to-br from-emerald-500 to-teal-600 text-white shadow-xs shrink-0">
                <Database className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                  {t("Fonte de Inteligência")}
                </h2>
                <p className="text-sm text-gray-500 leading-none mt-1">
                  {t("Como a IA deve construir suas postagens?")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <button
                type="button"
                onClick={() => setSourceMode("library")}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-200 ${
                  sourceMode === "library"
                    ? "border-emerald-600 bg-emerald-50/40"
                    : "border-gray-100 bg-gray-50/50 hover:border-emerald-200"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${sourceMode === "library" ? "bg-emerald-600 text-white" : "bg-white text-gray-400"}`}
                >
                  <Database className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {t("IA Guiada por Conteúdos")}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {t("Usa seus materiais salvos como base")}
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSourceMode("creative")}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-200 ${
                  sourceMode === "creative"
                    ? "border-purple-600 bg-purple-50/40"
                    : "border-gray-100 bg-gray-50/50 hover:border-purple-200"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${sourceMode === "creative" ? "bg-purple-600 text-white" : "bg-white text-gray-400"}`}
                >
                  <Star className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {t("IA Criativa Pura")}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {t("Gera conteúdo do zero a partir do nicho")}
                  </p>
                </div>
              </button>
            </div>

            {/* Detalhes da Biblioteca (Aparece se Modo Biblioteca selecionado) */}
            {sourceMode === "library" && (
              <div className="pt-5 border-t border-gray-100 animate-in fade-in slide-in-from-top-4 duration-500">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2.5">
                  {t("Selecione seus conteúdos")}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex flex-row flex-wrap gap-2 items-center mb-3">
                  <div className="flex-1 min-w-[160px]">
                    <Input
                      type="text"
                      value={contentFilter.search}
                      onChange={(e) =>
                        setContentFilter((prev) => ({
                          ...prev,
                          search: e.target.value,
                        }))
                      }
                      placeholder={t("Buscar seus conteúdos...")}
                      icon={<Search className="h-4 w-4" />}
                      iconPosition="left"
                      inputSize="sm"
                    />
                  </div>
                  <div className="w-[200px] shrink-0">
                    <Select
                      value={contentFilter.content_type}
                      onChange={(value) =>
                        setContentFilter((prev) => ({
                          ...prev,
                          content_type: value,
                        }))
                      }
                      options={[
                        {
                          value: "",
                          label: t("Todos os formatos"),
                          icon: <ListFilter />,
                        },
                        { value: "url", label: t("Link Web"), icon: <Link2 /> },
                        {
                          value: "text",
                          label: t("Texto/Nota"),
                          icon: <FileText />,
                        },
                        {
                          value: "file",
                          label: t("Documento"),
                          icon: <FileText />,
                        },
                      ]}
                      size="sm"
                    />
                  </div>
                </div>

                <div className="space-y-1.5 max-h-[210px] overflow-y-auto mb-4 pr-2 custom-scrollbar">
                  {loadingContents ? (
                    <div className="space-y-1.5 animate-pulse">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-100"
                        >
                          <Skeleton
                            variant="rectangular"
                            width={28}
                            height={28}
                            className="rounded-md shrink-0"
                          />
                          <Skeleton
                            variant="text"
                            width="25%"
                            height="0.875rem"
                            className="shrink-0"
                          />
                          <Skeleton
                            variant="text"
                            width="45%"
                            height="0.875rem"
                            className="flex-1"
                          />
                          <Skeleton
                            variant="rectangular"
                            width={60}
                            height={16}
                            className="rounded-sm shrink-0"
                          />
                        </div>
                      ))}
                    </div>
                  ) : availableContents.length === 0 ? (
                    <EmptyState
                      icon={<Database className="h-12 w-12 text-gray-300" />}
                      title={t("Biblioteca Vazia")}
                      description={t(
                        "Adicione materiais na sua biblioteca de conteúdos para usar este modo.",
                      )}
                      action={
                        <Button
                          onClick={() => loadContentLibrary()}
                          variant="secondary"
                        >
                          {t("Recarregar")}
                        </Button>
                      }
                    />
                  ) : (
                    availableContents
                      .filter((content) => {
                        const matchesSearch =
                          !contentFilter.search ||
                          content.name
                            .toLowerCase()
                            .includes(contentFilter.search.toLowerCase());
                        return (
                          matchesSearch &&
                          (!contentFilter.content_type ||
                            content.type === contentFilter.content_type)
                        );
                      })
                      .map((content) => {
                        const isSelected = selectedContentIds.includes(
                          content.id,
                        );
                        return (
                          <div
                            key={content.id}
                            onClick={() => handleContentSelection(content.id)}
                            className={`group flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                              isSelected
                                ? "border-emerald-500 bg-emerald-50/50"
                                : "border-gray-100 bg-white hover:border-emerald-200 hover:bg-gray-50/50"
                            }`}
                          >
                            <div
                              className={`flex items-center justify-center w-7 h-7 rounded-md shrink-0 transition-colors ${isSelected ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-400 group-hover:bg-emerald-100 group-hover:text-emerald-500"}`}
                            >
                              {content.type === "url" ? (
                                <Globe className="w-3.5 h-3.5" />
                              ) : (
                                <FileText className="w-3.5 h-3.5" />
                              )}
                            </div>
                            <span className="text-sm font-semibold text-gray-900 truncate shrink-0 max-w-[35%]">
                              {content.name}
                            </span>
                            <span className="text-xs text-gray-400 truncate flex-1 min-w-0">
                              {content.content_preview}
                            </span>
                            <Badge
                              variant="secondary"
                              className="text-[9px] font-bold uppercase tracking-wide shrink-0"
                            >
                              {content.type === "url"
                                ? t("Link Web")
                                : content.type === "text"
                                  ? t("Texto/Nota")
                                  : content.type === "file"
                                    ? t("Documento")
                                    : content.type}
                            </Badge>
                            {isSelected && (
                              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                            )}
                          </div>
                        );
                      })
                  )}
                </div>

                {selectedContentIds.length > 0 && (
                  <div>
                    <Select
                      label={t("Estratégia de Distribuição")}
                      value={distributionStrategy}
                      onChange={(value) =>
                        setDistributionStrategy(value as any)
                      }
                      options={distributionOptions.map((opt) => ({
                        value: opt.id,
                        label: `${opt.name} - ${opt.description}`,
                      }))}
                      size="sm"
                      hint={t(
                        "Define como os materiais selecionados serão priorizados e integrados no cronograma.",
                      )}
                    />
                  </div>
                )}
              </div>
            )}

            {sourceMode === "creative" && (
              <div className="pt-5 border-t border-gray-100 animate-in fade-in slide-in-from-top-4 duration-500">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-indigo-50/40 border border-indigo-100">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-600 text-white shrink-0">
                    <Star className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {t("Nenhum material necessário")}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t(
                        'A IA vai pesquisar tendências do seu nicho e criar o conteúdo do zero, sem depender da sua biblioteca. Você ainda pode refinar o resultado em "Personalização do Motor IA" abaixo.',
                      )}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <div className="p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 mb-2">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <p className="text-xs font-semibold text-gray-900">
                      {t("Tendências do Nicho")}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {t("Pesquisa temas em alta no seu segmento.")}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 mb-2">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <p className="text-xs font-semibold text-gray-900">
                      {t("Ganchos Persuasivos")}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {t("Aberturas com alto potencial de engajamento.")}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 mb-2">
                      <Zap className="w-4 h-4" />
                    </div>
                    <p className="text-xs font-semibold text-gray-900">
                      {t("Pronto em Segundos")}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {t("Sem preparar materiais com antecedência.")}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
        {/* SEÇÃO 3: REFINAMENTO DO MOTOR DE IA (Antigo Passo 4) */}
        <Card
          variant="default"
          className="mb-2 p-5 sm:p-6 rounded-2xl border-purple-200 shadow-xl shadow-gray-200/5 bg-white relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 p-4 sm:p-8 opacity-5">
            <Target className="w-24 h-24 sm:w-32 sm:h-32" />
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced((prev) => !prev)}
            aria-expanded={showAdvanced}
            className="w-full flex items-center gap-3 mb-0 text-left -m-2 p-2 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-linear-to-br from-purple-500 to-indigo-600 text-white shadow-xs shrink-0">
              <Target className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                  {t("Personalização do Motor IA")}
                </h3>
                <span className="text-[10px] font-black text-purple-700 bg-purple-100 px-2 py-0.5 rounded-md uppercase tracking-widest">
                  {t("Opcional")}
                </span>
              </div>
              <p className="text-sm text-gray-500 leading-none mt-1">
                {t(
                  "Aumenta a precisão da IA: persona, objetivo, modelo e tom de voz",
                )}
              </p>
            </div>
            <ChevronDown
              className={`h-5 w-5 text-gray-400 shrink-0 transition-transform duration-200 ${showAdvanced ? "rotate-180" : ""}`}
            />
          </button>
          <div
            className={
              showAdvanced ? "pt-6 mt-6 border-t border-purple-200" : "hidden"
            }
          >
            {/* Grupo 1: Configurações rápidas (3 selects, mesma altura) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <Select
                label={t("Tipo de Campanha")}
                value={campaignType}
                onChange={(value) => setCampaignType(value as any)}
                options={[
                  { value: "organic", label: t("Orgânica"), icon: <Globe /> },
                  {
                    value: "paid",
                    label: t("Paga"),
                    icon: <DollarSign />,
                  },
                ]}
                size="sm"
                hint={t(
                  "Define se o conteúdo será otimizado para alcance de comunidade ou conversão em anúncios.",
                )}
              />
              {campaignType === "paid" && (
                <Input
                  type="number"
                  label={t("Orçamento Diário Padrão (R$)")}
                  min={1}
                  value={dailyBudget}
                  onChange={(e) =>
                    setDailyBudget(Math.max(1, Number(e.target.value) || 1))
                  }
                  inputSize="sm"
                  hint={t(
                    "Usado em cada campanha-rascunho criada — revise e ajuste antes de lançar.",
                  )}
                />
              )}
              <Select
                label={t("Modelo de IA (Cérebro)")}
                value={aiModel}
                onChange={(value) => setAiModel(value)}
                options={[
                  {
                    value: "gpt-5.4-nano",
                    label: t("OpenAI - GPT-5.4 Nano (Padrão)"),
                    icon: <Sparkles />,
                  },
                  {
                    value: "gpt-5.1",
                    label: t("OpenAI - GPT-5.1 Flagship"),
                    icon: <Sparkles />,
                  },
                  {
                    value: "gpt-5.4-mini",
                    label: t("OpenAI - GPT-5.4 Mini"),
                    icon: <Sparkles />,
                  },
                  {
                    value: "gpt-4o",
                    label: t("OpenAI - GPT-4o Optimized"),
                    icon: <Sparkles />,
                  },
                  {
                    value: "claude-3.5-sonnet",
                    label: t("Anthropic - Claude 3.5 Sonnet"),
                    icon: <Brain />,
                  },
                  {
                    value: "claude-3.5-haiku",
                    label: t("Anthropic - Claude 3.5 Haiku"),
                    icon: <Brain />,
                  },
                  {
                    value: "deepseek-v4-flash",
                    label: t("DeepSeek - DeepSeek V4 Flash"),
                    icon: <Zap />,
                  },
                  {
                    value: "deepseek-v4-pro",
                    label: t("DeepSeek - DeepSeek V4 Pro"),
                    icon: <Zap />,
                  },
                  {
                    value: "gemini-1.5-pro",
                    label: t("Google - Gemini 1.5 Pro"),
                    icon: <Globe />,
                  },
                  {
                    value: "gemini-1.5-flash",
                    label: t("Google - Gemini 1.5 Flash"),
                    icon: <Globe />,
                  },
                  {
                    value: "kimik2-chat",
                    label: t("Kimi AI - Kimi Chat (Moonshot)"),
                    icon: <Star />,
                  },
                ]}
                size="sm"
                hint={t("Modelos de IA disponíveis para escolha.")}
              />
              <div>
                <Select
                  label={t("Estilo de Criador (Benchmark)")}
                  value={selectedCreatorBenchmark}
                  onChange={(value) => setSelectedCreatorBenchmark(value)}
                  options={[
                    {
                      value: "",
                      label: t("Sem Benchmark - Padrão do Nicho"),
                    },
                    ...creatorBenchmarks.map((b) => ({
                      value: b.creator_name,
                      label: `${b.creator_name.charAt(0).toUpperCase() + b.creator_name.slice(1)} (${b.tags.filter((tag: string) => tag !== b.creator_name).join(", ") || t("Reels")})`,
                    })),
                  ]}
                  size="sm"
                  hint={t("Criador de referência pro estilo de escrita.")}
                />
                {creatorBenchmarksError && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-600">
                    {t("Não foi possível carregar os benchmarks.")}
                    <button
                      type="button"
                      onClick={fetchCreatorBenchmarks}
                      className="font-semibold underline hover:text-amber-700"
                    >
                      {t("Recarregar")}
                    </button>
                  </p>
                )}
              </div>
            </div>

            {/* Creative AI no cronograma: copy + imagem (Brand Kit/ICP/revisor) via
                CreativeBriefCrew em vez do gerador de texto legado. */}
            <div className="mb-5 rounded-2xl border border-indigo-200 bg-linear-to-br from-indigo-50/70 to-purple-50/70 p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer group/check">
                <div
                  className={`mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 transition-all ${generateCreativeAi ? "bg-indigo-600 border-indigo-600" : "border-gray-300 bg-white group-hover/check:border-indigo-400"}`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={generateCreativeAi}
                    onChange={(e) => setGenerateCreativeAi(e.target.checked)}
                  />
                  {generateCreativeAi && (
                    <CheckCircle className="w-2.5 h-2.5 text-white" />
                  )}
                </div>
                <div>
                  <span className="flex items-center gap-1.5 text-sm font-bold text-indigo-900">
                    <Sparkles className="h-3.5 w-3.5" />
                    {t(
                      "Gerar criativo com Creative AI (copy + imagem + revisor)",
                    )}
                  </span>
                  <p className="text-xs text-indigo-500 font-medium mt-0.5">
                    {t(
                      "Cada post gerado já sai com imagem (Brand Kit/ICP aplicados). Sem isso, o cronograma gera só texto, como hoje.",
                    )}
                  </p>
                </div>
              </label>

              {generateCreativeAi && (
                <div className="pt-2 border-t border-indigo-100 space-y-2">
                  <p className="text-[11px] text-indigo-500 font-medium">
                    {t(
                      'O formato do criativo é escolhido automaticamente pela rede de cada post: Instagram Feed, LinkedIn Post, Facebook Post etc. — de acordo com as redes selecionadas em "Redes Sociais Ativas".',
                    )}
                  </p>
                  <p className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    {t(
                      "Gera 1 imagem por post (custo de crédito maior que só texto) e as imagens vão ficando prontas aos poucos em segundo plano — acompanhe em /posts. Limite de {max} posts por geração com isso ligado.",
                      { max: CREATIVE_AI_MAX_POSTS },
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Grupo 2: Direcionamento e conteúdo (grid 2 colunas) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Textarea
                  label={t("Persona / ICP")}
                  labelRight={
                    <CharCounter
                      current={persona.length}
                      recommended={PERSONA_LIMIT.recommended}
                      hard={PERSONA_LIMIT.hard}
                    />
                  }
                  value={persona}
                  onChange={(e) => setPersona(e.target.value)}
                  maxLength={PERSONA_LIMIT.hard}
                  rows={2}
                  inputSize="sm"
                  disabled={useRegisteredIcp}
                  className="rounded-xl resize-none"
                  placeholder={t(
                    "Ex: Empreendedores digitais que buscam automação...",
                  )}
                  hint={
                    useRegisteredIcp
                      ? t(
                          "Ignorado — usando o ICP cadastrado do cliente/organização.",
                        )
                      : t("Calibra vocabulário e tom pro seu cliente ideal.")
                  }
                />
                <label className="mt-2 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50/70 text-xs font-medium text-gray-700 cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-colors group/check">
                  <div
                    className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 transition-all ${useRegisteredIcp ? "bg-purple-600 border-purple-600" : "border-gray-300 bg-white group-hover/check:border-purple-400"}`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={useRegisteredIcp}
                      onChange={(e) => setUseRegisteredIcp(e.target.checked)}
                    />
                    {useRegisteredIcp && (
                      <CheckCircle className="w-2.5 h-2.5 text-white" />
                    )}
                  </div>
                  <span>{t("Usar ICP cadastrado do cliente")}</span>
                </label>
              </div>
              <Textarea
                label={t("Produto/Serviço (opcional)")}
                labelRight={
                  <CharCounter
                    current={product.length}
                    recommended={PRODUCT_LIMIT.recommended}
                    hard={PRODUCT_LIMIT.hard}
                  />
                }
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                maxLength={PRODUCT_LIMIT.hard}
                rows={2}
                inputSize="sm"
                className="rounded-xl resize-none"
                placeholder={t("Ex: Virtus AI")}
                hint={t(
                  "Produto/serviço a mencionar naturalmente nos posts gerados.",
                )}
              />
              <Textarea
                label={t("Objetivos da Estratégia")}
                labelRight={
                  <CharCounter
                    current={objectives.length}
                    recommended={OBJECTIVES_LIMIT.recommended}
                    hard={OBJECTIVES_LIMIT.hard}
                  />
                }
                value={objectives}
                onChange={(e) => setObjectives(e.target.value)}
                maxLength={OBJECTIVES_LIMIT.hard}
                rows={2}
                inputSize="sm"
                className="rounded-xl resize-none"
                placeholder={t(
                  "Ex: Aumentar o engajamento e gerar autoridade no nicho...",
                )}
                hint={t("Guia a abordagem e o CTA de cada post.")}
              />
              <Textarea
                label={t("Fontes de Pesquisa")}
                labelRight={
                  <span
                    className={`text-xs font-semibold ${sources.split("\n").filter((s) => s.trim()).length > 3 ? "text-red-500" : "text-gray-400"}`}
                  >
                    {sources.split("\n").filter((s) => s.trim()).length}/3
                  </span>
                }
                value={sources}
                onChange={(e) => {
                  const val = e.target.value;
                  const lines = val.split("\n");
                  if (lines.length <= 3) {
                    setSources(val);
                  } else {
                    setSources(lines.slice(0, 3).join("\n"));
                  }
                }}
                rows={2}
                inputSize="sm"
                className="rounded-xl resize-none"
                placeholder="https://..."
                hint={t("Links que a IA vai 'estudar' pro contexto.")}
              />
              <Textarea
                label={t("Horários de Publicação")}
                value={timeSlotsRaw}
                onChange={(e) => {
                  setTimeSlotsRaw(e.target.value);
                  setTimeSlotsWarning(null);
                }}
                onBlur={() => {
                  const lines = timeSlotsRaw
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean);
                  const validSlots = lines.filter((line) =>
                    /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(line),
                  );
                  const invalidCount = lines.length - validSlots.length;
                  const finalSlots =
                    validSlots.length > 0 ? validSlots : ["09:00"];
                  setTimeSlots(finalSlots);
                  setTimeSlotsRaw(finalSlots.join("\n"));
                  setTimeSlotsWarning(
                    invalidCount > 0
                      ? t(
                          "{count} horário(s) inválido(s) foram ignorados (use o formato HH:MM).",
                          { count: invalidCount },
                        )
                      : null,
                  );
                }}
                rows={2}
                inputSize="sm"
                className="rounded-xl resize-none"
                placeholder="09:00"
                warning={timeSlotsWarning || undefined}
                hint={
                  timeSlotsWarning
                    ? undefined
                    : t("Horários de maior impacto (auto-balanceado).")
                }
              />
            </div>

            {/* Configuração geral da seção - não pertence a nenhum campo específico */}
            <label className="mt-5 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50/70 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-colors group/check">
              <div
                className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 transition-all ${includeUTM ? "bg-purple-600 border-purple-600" : "border-gray-300 bg-white group-hover/check:border-purple-400"}`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={includeUTM}
                  onChange={(e) => setIncludeUTM(e.target.checked)}
                />
                {includeUTM && (
                  <CheckCircle className="w-2.5 h-2.5 text-white" />
                )}
              </div>
              <span>{t("Incluir UTM padrão nos links dos posts")}</span>
            </label>
          </div>
        </Card>

        <Card
          variant="default"
          className="relative p-5 sm:p-6 rounded-2xl border-0 bg-linear-to-br from-purple-700 via-purple-600 to-indigo-700 shadow-xl overflow-hidden"
        >
          {/* Decoração: estrelas */}
          <Sparkles className="absolute top-4 right-6 w-4 h-4 text-white/40" />
          <Sparkles className="absolute bottom-5 right-16 w-3 h-3 text-white/30" />

          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/15 border border-white/20 text-2xl shadow-lg shrink-0">
                🚀
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-black text-white tracking-tight">
                  {t("Pronto para gerar seu cronograma")}
                </h3>
                <p className="text-sm text-purple-100">
                  {t("Gerando")}{" "}
                  <span className="font-bold text-white">
                    {calculateTotalPosts()}
                  </span>{" "}
                  {t("postagens que entrarão como drafts para sua revisão.")}
                </p>
                {(() => {
                  const estimatedCost = calculateEstimatedCreditCost();
                  return estimatedCost !== null ? (
                    <p className="text-xs text-purple-200 mt-1">
                      {t("Custo estimado: ~{cost} créditos", {
                        cost: estimatedCost,
                      })}
                    </p>
                  ) : null;
                })()}
              </div>
            </div>

            <Button
              onClick={generateSuggestions}
              disabled={
                loading ||
                platforms.length === 0 ||
                (useContentLibraryEnabled && selectedContentIds.length === 0)
              }
              variant="primary"
              size="md"
              className="w-full lg:w-auto px-8 h-12 rounded-xl bg-white! hover:bg-purple-50! text-purple-700! font-black shadow-lg shadow-black/20 hover:shadow-xl active:scale-95 transition-all text-sm shrink-0 disabled:bg-white/70! disabled:text-purple-400!"
              isLoading={loading}
              icon={!loading ? <Sparkles className="h-4 w-4" /> : undefined}
            >
              {loading
                ? t("Processando Estratégia")
                : t("Gerar Cronograma com IA")}
            </Button>
          </div>

          {(() => {
            const blockingReasons: string[] = [];
            if (platforms.length === 0) {
              blockingReasons.push(
                t("Selecione ao menos uma rede social ativa"),
              );
            }
            if (useContentLibraryEnabled && selectedContentIds.length === 0) {
              blockingReasons.push(
                t("Selecione pelo menos um conteúdo da biblioteca"),
              );
            }
            if (blockingReasons.length === 0) return null;
            return (
              <div className="relative mt-5 flex items-center justify-center gap-2 p-3 bg-white/95 rounded-xl shadow-xs">
                <ArrowRight className="w-4 h-4 text-amber-600 animate-pulse shrink-0" />
                <span className="text-xs font-semibold text-amber-700">
                  {blockingReasons.join(" · ")}
                </span>
              </div>
            );
          })()}
        </Card>

        {/* Mensagens de erro/sucesso */}
        {error && (
          <Alert
            variant="error"
            title={t("Erro")}
            dismissible
            onDismiss={() => setError(null)}
            className="mb-4"
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            variant="success"
            title={t("Sucesso")}
            dismissible
            onDismiss={() => setSuccess(null)}
            className="mb-4"
          >
            {success}
          </Alert>
        )}
        {/* Resultados */}
        {suggestions &&
          suggestions.schedule_suggestions &&
          suggestions.schedule_suggestions.length > 0 && (
            <div className="mt-12 sm:mt-16">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-2xl bg-indigo-50 text-indigo-600">
                    <Calendar className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                      {t("Estratégia Mapeada")}
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-500 leading-none mt-1">
                      {t("Cronograma gerado pela inteligência virtus")}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {platforms.map((p) => (
                    <Badge
                      key={p}
                      variant="secondary"
                      className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide bg-white border-purple-200 shadow-xs"
                    >
                      {t(getPlatformName(p))}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-8">
                {Array.from({ length: weeks }, (_, weekIndex) => (
                  <div key={weekIndex} className="relative">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="h-px flex-1 bg-linear-to-r from-transparent to-gray-200" />
                      <div className="px-5 py-2 rounded-full bg-gray-900 border border-purple-800 text-[10px] font-bold text-white uppercase tracking-wide">
                        {t("Semana {number}", { number: weekIndex + 1 })}
                      </div>
                      <div className="h-px flex-1 bg-linear-to-l from-transparent to-gray-200" />
                    </div>

                    <ResponsiveGrid cols={{ default: 1, lg: 2 }} gap="md">
                      {(suggestions?.schedule_suggestions || [])
                        .filter(
                          (suggestion) =>
                            suggestion.week_number === weekIndex + 1,
                        )
                        .map((suggestion, index) => (
                          <Card
                            key={index}
                            variant="default"
                            className="p-6 rounded-2xl border-purple-200 bg-white hover:shadow-2xl hover:shadow-purple-500/5 transition-all duration-500 group relative overflow-hidden"
                          >
                            <div
                              className={`absolute top-0 right-0 w-32 h-32 opacity-[0.03] -mr-16 -mt-16 rounded-full ${getPlatformColor(suggestion.platform)}`}
                            />

                            <div className="flex items-center justify-between mb-6">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`p-2 rounded-xl text-white shadow-lg ${getPlatformColor(suggestion.platform)}`}
                                >
                                  <div className="w-5 h-5 flex items-center justify-center font-bold text-[10px]">
                                    {getPlatformName(
                                      suggestion.platform,
                                    )[0].toUpperCase()}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide leading-none mb-1">
                                    {t(getPlatformName(suggestion.platform))} •{" "}
                                    {suggestion.day_of_week}
                                  </div>
                                  <div className="text-sm font-bold text-gray-900 tracking-tight">
                                    {suggestion.time}
                                  </div>
                                </div>
                              </div>
                              <Badge
                                className={`${getEngagementColor(suggestion.engagement_prediction)}text-[9px] font-bold uppercase tracking-wide px-3 py-1 rounded-full`}
                              >
                                {t("Engajamento {prediction}", {
                                  prediction: t(
                                    suggestion.engagement_prediction,
                                  ),
                                })}
                              </Badge>
                            </div>

                            <div className="mb-6">
                              <div className="flex flex-wrap gap-2 mb-3">
                                <Badge
                                  variant="outline"
                                  className="text-[9px] font-bold uppercase tracking-wide border-purple-200 text-purple-600 bg-purple-50/50"
                                >
                                  {suggestion.content_type}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="text-[9px] font-bold uppercase tracking-wide border-purple-200 text-indigo-600 bg-indigo-50/50"
                                >
                                  {suggestion.theme}
                                </Badge>
                              </div>
                              <textarea
                                className="w-full min-h-[140px] text-sm font-bold text-gray-600 bg-gray-50/50rounded-2xl p-4 focus:ring-2 focus:ring-purple-500/10 focus:bg-white transition-all duration-300 resize-none leading-relaxed italic"
                                value={
                                  editedContent[`${weekIndex}-${index}`] ??
                                  suggestion.suggested_content
                                }
                                onChange={(e) =>
                                  setEditedContent((prev) => ({
                                    ...prev,
                                    [`${weekIndex}-${index}`]: e.target.value,
                                  }))
                                }
                              />
                            </div>

                            <div className="flex flex-wrap gap-1.5 mb-6">
                              {(suggestion.hashtags || []).map(
                                (hashtag, idx) => (
                                  <span
                                    key={idx}
                                    className="text-[10px] font-bold text-indigo-500 bg-indigo-50 px-2 py-1 rounded-lg border border-purple-200 hover:bg-indigo-100 transition-colors cursor-default"
                                  >
                                    #{hashtag}
                                  </span>
                                ),
                              )}
                            </div>

                            <div className="space-y-3 mb-6">
                              <div className="flex items-start gap-3 p-3 bg-emerald-50/50 rounded-xl border border-emerald-100/50">
                                <Target className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                                <p className="text-[11px] font-bold text-emerald-700 leading-tight">
                                  <span className="block text-[9px] font-bold uppercase tracking-wide opacity-60 mb-1">
                                    {t("Call to Action:")}
                                  </span>
                                  {suggestion.call_to_action}
                                </p>
                              </div>
                              <div className="flex items-start gap-3 p-3 bg-amber-50/50 rounded-xl border border-amber-100/50">
                                <Star className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                                <p className="text-[11px] font-bold text-amber-700 leading-tight">
                                  <span className="block text-[9px] font-bold uppercase tracking-wide opacity-60 mb-1">
                                    {t("Dica de Ouro:")}
                                  </span>
                                  {suggestion.best_practice}
                                </p>
                              </div>
                            </div>

                            <Button
                              fullWidth
                              variant="primary"
                              className="h-12 rounded-xl bg-gray-900 hover:bg-black text-white text-[10px] font-bold uppercase tracking-wide shadow-xl shadow-gray-900/10 transition-all hover:scale-[1.02] active:scale-95"
                              onClick={async () => {
                                try {
                                  const token =
                                    localStorage.getItem("access_token") ||
                                    localStorage.getItem("token");
                                  if (!token) {
                                    setError(t("Token não encontrado"));
                                    return;
                                  }
                                  const body = {
                                    content:
                                      editedContent[`${weekIndex}-${index}`] ??
                                      suggestion.suggested_content,
                                    platforms: [suggestion.platform],
                                    business_type:
                                      suggestion.theme || "general",
                                    auto_save: true,
                                  };
                                  await apiService.post(
                                    API_ENDPOINTS.CONTENT_SELECTOR
                                      .GENERATE_SOCIAL_MEDIA_POST,
                                    body,
                                  );
                                  setSuccess(
                                    t(
                                      'Post enviado para "Posts Pendentes" com sucesso!',
                                    ),
                                  );
                                } catch (e) {
                                  setError(
                                    e instanceof Error
                                      ? e.message
                                      : t("Erro ao criar post"),
                                  );
                                }
                              }}
                            >
                              {t("Materializar este Post")}
                            </Button>
                          </Card>
                        ))}
                    </ResponsiveGrid>
                  </div>
                ))}
              </div>

              {/* Melhores Práticas Gerais */}
              {suggestions?.best_practices_applied &&
                suggestions.best_practices_applied.length > 0 && (
                  <Card
                    variant="glass"
                    className="mt-10 p-6 rounded-2xl border-emerald-100 bg-emerald-50/20"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-2.5 rounded-xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/20">
                        <CheckCircle className="h-5 w-5" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                        {t("Estratégias de Refinamento Aplicadas")}
                      </h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {(suggestions?.best_practices_applied || []).map(
                        (practice, index) => (
                          <div
                            key={index}
                            className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-emerald-100/50 shadow-xs"
                          >
                            <Star className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                            <span className="text-xs font-bold text-gray-600 leading-relaxed">
                              {practice}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  </Card>
                )}

              {/* Call to Action Final */}
              <div className="mt-8 p-4 sm:p-5 rounded-2xl border border-purple-200 bg-purple-50/40 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-bold text-gray-900 tracking-tight">
                    {t("Pipeline Estratégico Atualizado")}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t(
                      "As postagens já estão na lista de espera, prontas para virar resultados reais.",
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => navigate("/posts?status=pending")}
                  variant="primary"
                  size="sm"
                  className="h-11 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-xs w-full sm:w-auto shrink-0"
                  icon={<ArrowRight className="h-4 w-4" />}
                  iconPosition="right"
                >
                  {t("Ver Posts Pendentes")}
                </Button>
              </div>
            </div>
          )}
      </div>
    </PageLayout>
  );
};

export default ScheduleSuggestions;
